import { createElement } from '../../core/dom/helpers.js';
import { isAbortError } from '../../core/http/is-abort-error.js';

// ---------------------------------------------------------------------------
// Tag editor dialog — pick existing tags + add new ones for a file
// ---------------------------------------------------------------------------
//
// Presentation + interaction only. Receives `loadTags(signal)` and
// `createTag(name, signal)` callbacks (no apiClient/adapter import), so it
// stays in the components layer. Returns a HANDLE `{ promise, destroy }` so the
// host can tear an open dialog down on page destroy while async is pending.
//
// Resolves with the chosen relation list `{ id, name }[]`, or `null` on
// cancel/escape/destroy. Existing options (`{ value, label }`) are normalized
// to `{ id: value, name: label }`; newly created tags use their raw `{id,name}`
// row verbatim. Dedupe by stringified id; a typed name matching an existing
// option label (case-insensitive, trimmed) selects that option instead of
// creating a duplicate.

/**
 * @typedef {{ id: string|number, name: string }} TagRelation
 * @typedef {import('../../contracts/forms.js').FieldOption} FieldOption
 */

/**
 * @typedef {Object} TagEditorOptions
 * @property {(signal: AbortSignal) => Promise<FieldOption[]>} loadTags - Load selectable tag options.
 * @property {(name: string, signal: AbortSignal) => Promise<TagRelation>} createTag - Create a new tag, returns the raw row.
 * @property {TagRelation[]} [current] - The file's current tags (pre-checked by id).
 * @property {string} [title] - Dialog title. Default "Edit tags".
 */

/**
 * @typedef {Object} TagEditorHandle
 * @property {Promise<TagRelation[] | null>} promise
 * @property {() => void} destroy
 */

/**
 * Open the tag editor dialog.
 *
 * @param {TagEditorOptions} options
 * @returns {TagEditorHandle}
 */
export function tagEditorDialog(options) {
  const { loadTags, createTag, current = [], title = 'Edit tags' } = options;

  const ac = new AbortController();
  let settled = false;
  /** @type {(value: TagRelation[] | null) => void} */
  let resolveFn = () => {};
  const promise = /** @type {Promise<TagRelation[] | null>} */ (
    new Promise((resolve) => { resolveFn = resolve; })
  );

  // Working selection: stringified id → relation.
  /** @type {Map<string, TagRelation>} */
  const selected = new Map();
  for (const t of current) selected.set(String(t.id), { id: t.id, name: t.name });

  /** @type {FieldOption[]} */
  let loadedOptions = [];
  let createPending = false;

  // --- DOM scaffold (mirrors confirm-dialog / media-picker lifecycle) ---
  const overlay = createElement('div', { class: 'tag-editor__overlay' });
  const panel = createElement('div', {
    class: 'tag-editor__panel', role: 'dialog', 'aria-modal': 'true', 'aria-label': title,
  });
  const titleEl = createElement('h3', { class: 'tag-editor__title' }, [title]);
  const list = createElement('div', { class: 'tag-editor__list', role: 'group', 'aria-label': 'Existing tags' });
  const status = createElement('div', { class: 'tag-editor__status' });

  const addRow = createElement('div', { class: 'tag-editor__add' });
  const addInput = /** @type {HTMLInputElement} */ (createElement('input', {
    type: 'text', class: 'tag-editor__add-input', placeholder: 'Add new tag…',
  }));
  const addBtn = /** @type {HTMLButtonElement} */ (createElement('button', { type: 'button', class: 'btn tag-editor__add-btn' }, ['Add']));
  addRow.appendChild(addInput);
  addRow.appendChild(addBtn);

  const footer = createElement('div', { class: 'tag-editor__footer' });
  const cancelBtn = createElement('button', { type: 'button', class: 'btn tag-editor__cancel' }, ['Cancel']);
  const saveBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button', class: 'btn btn-primary tag-editor__save',
  }, ['Save']));
  footer.appendChild(cancelBtn);
  footer.appendChild(saveBtn);

  panel.appendChild(titleEl);
  panel.appendChild(list);
  panel.appendChild(addRow);
  panel.appendChild(status);
  panel.appendChild(footer);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  const previousFocus = /** @type {HTMLElement | null} */ (document.activeElement);
  const savedOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  /** @param {boolean} busy */
  function setSaveDisabled(busy) {
    saveBtn.disabled = busy;
  }

  /** @param {string} msg */
  function showError(msg) {
    status.textContent = msg;
    status.classList.add('tag-editor__status--error');
  }
  function clearError() {
    status.textContent = '';
    status.classList.remove('tag-editor__status--error');
  }

  /**
   * Options to render = loaded options PLUS any currently-selected tag NOT in
   * the loaded set (e.g. a current tag beyond the first options page). Without
   * this, such a tag would stay invisibly selected — unremovable and saved back.
   * @returns {FieldOption[]}
   */
  function displayOptions() {
    const seen = new Set(loadedOptions.map(o => String(o.value)));
    /** @type {FieldOption[]} */
    const extras = [];
    for (const [id, rel] of selected) {
      if (!seen.has(id)) extras.push({ value: id, label: rel.name });
    }
    return [...loadedOptions, ...extras];
  }

  /** Render the existing-tag checkbox list from displayOptions + selected state. */
  function renderList() {
    list.textContent = '';
    for (const opt of displayOptions()) {
      const id = String(opt.value);
      const labelEl = createElement('label', { class: 'tag-editor__item' });
      const cb = /** @type {HTMLInputElement} */ (createElement('input', { type: 'checkbox' }));
      cb.checked = selected.has(id);
      cb.dataset.id = id;
      cb.dataset.name = opt.label;
      const text = createElement('span', { class: 'tag-editor__item-name' });
      text.textContent = opt.label; // textContent → safe
      labelEl.appendChild(cb);
      labelEl.appendChild(text);
      list.appendChild(labelEl);
    }
  }

  /** @param {Event} e */
  function onListChange(e) {
    const cb = /** @type {HTMLInputElement} */ (e.target);
    if (!(cb instanceof HTMLInputElement) || cb.type !== 'checkbox') return;
    const id = cb.dataset.id ?? '';
    // Use the checkbox's own id/name so merged current-tags (not in loadedOptions)
    // are still toggleable.
    if (cb.checked) selected.set(id, { id, name: cb.dataset.name ?? '' });
    else selected.delete(id);
  }

  function addTyped() {
    if (createPending) return;
    const name = addInput.value.trim();
    if (name === '') { addInput.focus(); return; } // whitespace-only → no-op
    clearError();

    // Match an existing option label case-insensitively → select it, no create.
    const existing = loadedOptions.find(o => o.label.trim().toLowerCase() === name.toLowerCase());
    if (existing) {
      selected.set(String(existing.value), { id: existing.value, name: existing.label });
      addInput.value = '';
      renderList();
      addInput.focus();
      return;
    }

    createPending = true;
    setSaveDisabled(true);
    addBtn.disabled = true;
    createTag(name, ac.signal).then((row) => {
      if (settled) return;
      createPending = false;
      addBtn.disabled = false;
      setSaveDisabled(false);
      // Use the returned raw {id,name} verbatim; add to options + selection.
      loadedOptions = [...loadedOptions, { value: String(row.id), label: row.name }];
      selected.set(String(row.id), { id: row.id, name: row.name });
      addInput.value = '';
      renderList();
      addInput.focus();
    }).catch((/** @type {unknown} */ err) => {
      if (settled) return;
      createPending = false;
      addBtn.disabled = false;
      setSaveDisabled(false);
      if (isAbortError(err)) return;
      showError('Could not create tag. Please try again.');
    });
  }

  // --- Lifecycle ---
  /** @param {TagRelation[] | null} result */
  function close(result) {
    if (settled) return;
    settled = true;
    ac.abort();
    document.body.style.overflow = savedOverflow;
    document.removeEventListener('keydown', onKeydown);
    overlay.remove();
    if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    resolveFn(result);
  }

  /** @param {KeyboardEvent} e */
  function onKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(null); return; }
    if (e.key === 'Tab') {
      const focusable = /** @type {HTMLElement[]} */ (
        [...panel.querySelectorAll('input:not(:disabled), button:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)')]
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  list.addEventListener('change', onListChange);
  addBtn.addEventListener('click', addTyped);
  addInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); addTyped(); } });
  cancelBtn.addEventListener('click', () => close(null));
  saveBtn.addEventListener('click', () => { if (!createPending) close([...selected.values()]); });
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(null); });
  document.addEventListener('keydown', onKeydown);

  // Initial load.
  setSaveDisabled(true);
  status.textContent = 'Loading tags…';
  loadTags(ac.signal).then((opts) => {
    if (settled) return;
    loadedOptions = Array.isArray(opts) ? opts : [];
    clearError();
    setSaveDisabled(false);
    renderList();
    addInput.focus();
  }).catch((/** @type {unknown} */ err) => {
    if (settled) return;
    if (isAbortError(err)) return;
    setSaveDisabled(false); // allow Save (keeps current selection) even if options failed
    // Even with no loaded options, displayOptions() still renders the current
    // selected tags — so the user can see and remove existing tags on failure.
    renderList();
    showError('Could not load tags.');
  });

  return {
    promise,
    destroy() { close(null); },
  };
}
